import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { switchMap, takeUntil, combineLatest, Subscription, timer, take } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

import { CmsService, Page, PageMeta, PageMetaService } from '@spartacus/core';

import { BrandsEnum, PositionEnum, SdSpyTargetDirective, ThemesEnum } from '@seb/web-design-system/core';
import { ITeaser, SdTeaserComponent } from '@seb/web-design-system/teaser';

import { BrandCornerMapper, HeadingParagraphMapper, LinkMapper, transformHeadlineLevelToTitleLevel } from '../../../mappers';
import { CustomMedia, TeaserComponentInterface, TeaserModuleInterface, teaserSkeleton } from '../../../models';
import { ChatService, HobStateService, ImageService, TrackingService } from '../../../services';
import { AbstractModuleComponent } from '../abstract-module.component';
import { TeaserModuleLayoutEnum } from '../../../enums';

@Component({
  selector: 'wc-teaser-module',
  templateUrl: './teaser-module.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdTeaserComponent,
    SdSpyTargetDirective
  ]
})
export class TeaserModuleComponent extends AbstractModuleComponent<TeaserModuleInterface> implements OnInit {
  textConfig: PositionEnum.left | PositionEnum.right | PositionEnum.center = PositionEnum.left;
  teasers: ITeaser[];
  autoplay = false;
  loop = false;
  titleLevel: number;
  context = 'Teaser';
  isLarge = false;
  hasAlphaHeader = false;
  activeBrand: BrandsEnum;
  isHob: boolean;

  private mediaContainerSubscriptions: Subscription[] = [];

  constructor(
    private imageService: ImageService,
    private cdRef: ChangeDetectorRef,
    private pageMetaService: PageMetaService,
    private brandCornerMapper: BrandCornerMapper,
    private cmsService: CmsService,
    private trackingService: TrackingService,
    private linkMapper: LinkMapper,
    private chatService: ChatService,
    private readonly headingParagraphMapper: HeadingParagraphMapper,
    private readonly hobStateService: HobStateService
  ) {
    super();
  }

  @ViewChild('teaserCmp')
  teaserCmp: SdTeaserComponent;

  override mandatoryComponentFunction = (cmp: TeaserComponentInterface): boolean => {
    return !!cmp.media || !!cmp.mediaContainer;
  };

  override onModuleInit(module: TeaserModuleInterface): void {
    this.setLayout(module);
    if (this.useLazyLoad) {
      const teasersLength = module.components ? Math.min(module.components.split(' ').length, 3) : 0;
      this.teasers = new Array(teasersLength).fill(undefined).map(
        (_, i) => ({ ...teaserSkeleton, id: `${i}` })
      );
    } else {
      this.teasers = [];
    }
    this.cdRef.markForCheck();
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.hobStateService.hobState$.subscribe(({ isHob, liveBrand, siteBrand }) => {
        this.activeBrand = liveBrand || siteBrand;
        this.isHob = isHob;
        this.cdRef.markForCheck();
      })
    );

    const module$ = this.module$.pipe(
      switchMap((module) =>
        this.componentService.getSubComponentsLazyComponentData(module, 'componentsObjects', 'links')
      )
    );

    const pageMeta$ = this.pageMetaService.getMeta();
    const page$ = this.cmsService.getCurrentPage();

    combineLatest([module$, pageMeta$, page$])
      .pipe(take(1))
      .subscribe(([module, pageMeta, page]: [TeaserModuleInterface, PageMeta, Page]) => {
        this.isLarge = page['headerDesign'] === 'ALPHA';
        this.hasAlphaHeader = page['headerDesign'] === 'ALPHA';
        this.context = pageMeta.title;
        this.setLayout(module);
        this.titleLevel = transformHeadlineLevelToTitleLevel(module.componentsObjects[0]?.heading?.headlineLevel);
        const teaserComponents = module.componentsObjects.slice(0, 3);
        this.teasers.splice(teaserComponents.length);
        if (teaserComponents.length === 0) {
          this.teasers = [];
          this.setLoaded();
        }
        teaserComponents.forEach((cmp, index) => {
          if (this.brandCorner) {
            cmp['brandCorner'] = this.brandCorner;
          }
          if (cmp.mediaContainer) {
            this.mediaContainerSubscriptions[index]?.unsubscribe();
            this.mediaContainerSubscriptions[index] = this.imageService
              .getMediaFromContainer(cmp.mediaContainer)
              .pipe(takeUntil(this.destroy$))
              .subscribe((customMedia: CustomMedia) => {
                this.addTeaser(index, cmp, customMedia);
              });
          } else {
            this.addTeaser(index, cmp);
          }
        });

        this.teasers = this.teasers.map((item) => {
          const { media } = item;
          let updatedMedia = { ...media };

          if (updatedMedia.mime && typeof updatedMedia.mime !== 'string') {
            updatedMedia.mime = '';
          }

          if (updatedMedia.mime?.includes('gif')) {
            updatedMedia = { ...updatedMedia, isGif: true };
          }

          return { ...item, media: updatedMedia };
        });

        this.autoplay = module.componentsObjects.some((cmp) => cmp.autoplay === 'true');
        this.loop = module.componentsObjects.some((cmp) => cmp.loop === 'true');
        if (isPlatformBrowser(this.platformId)) {
          timer(0).pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              this.teaserCmp?.teaserSlider?.refreshSlider();
            });
        }
        this.cdRef.markForCheck();
      });
  }

  private setLayout(module: TeaserModuleInterface): void {
    switch (module.layout) {
      case TeaserModuleLayoutEnum.BOTTOM_LEFT:
        this.textConfig = PositionEnum.left;
        break;
      case TeaserModuleLayoutEnum.BOTTOM_RIGHT:
        this.textConfig = PositionEnum.right;
        break;
      case TeaserModuleLayoutEnum.CENTER:
        this.textConfig = PositionEnum.center;
        break;
      default:
        this.textConfig = PositionEnum.left;
        break;
    }
  }

  private toTeaser(component: TeaserComponentInterface, customMedia?: CustomMedia): ITeaser {
    const brand = this.baseStoreService.getBrand(component['brandCorner']);
    const brandCorner = brand ? brand.id as BrandsEnum : BrandsEnum.default;

    component = {
      ...component,
      heading: {
        ...component.heading,
        textConfig: component.heading?.textConfig ? component.heading?.textConfig : this.textConfig
      }
    };

    const themesConfigBrand = this.isHob ? this.activeBrand : this.brandCorner || null;

    return {
      brand: brandCorner,
      content: {
        heading: this.headingParagraphMapper.toHeadingParagraph(component),
        footnote: component.footnote,
        linksImages: component.linksObjects
          ? component.linksObjects.filter((link) => !!link.media).map((link) => this.linkMapper.toLinkMedia(link))
          : [],
        links: component.linksObjects?.map((link) => LinkMapper.cmsLinkToIExtendedLink(link)) || [undefined]
      },
      media: customMedia
        ? this.imageService.normalizeMedia(customMedia)
        : this.imageService.normalizeMedia(component.media, component.videoFallback),
      themesConfig: this.brandCornerMapper.toWcConfig(themesConfigBrand)?.isThemeDark
        ? ThemesEnum.dark
        : ThemesEnum.light,
      isEmbedded: this.brandCorner
        ? this.brandCornerMapper.toWcConfig(this.brandCorner)?.isTeaserEmbedded
        : this.brandCornerMapper.toWcConfig(brandCorner)?.isTeaserEmbedded,
      isLoaded: false
    };
  }

  private addTeaser(index: number, cmp: TeaserComponentInterface, media?: CustomMedia) {
    this.teasers[index] = this.toTeaser(cmp, media);

    if (index === 0) {
      this.setLoaded();
    }
  }

  onClicked(event) {
    if (event?.link?.url === '<<chat>>') {
      this.chatService.clickOpenChat();
    }
    this.trackingService.sendEvent('carouselClick', {
      componentName: event.title,
      carouselAction: event.link.linkName,
      postion: event.position
    });
  }
}
